import React from 'react';
import VisaPageLayout2, { FaqItem, ProcessStep, RequirementItem, VisaCategory, VisaPlan } from './visa-page-layout-2';
import { useVisaPacksByCountry } from '../hooks/useVisaPacksByCountry';


const categories: VisaCategory[] = [
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s4-8 10-8 10 8 10 8-4 8-10 8-10-8-10-8z" /><circle cx="12" cy="12" r="3" /></svg>),
    title: 'B2 — Tourism & Visit',
    text: 'Vacation, sightseeing, or visiting friends and relatives in the United States.',
  },
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>),
    title: 'B1 — Business',
    text: 'Meetings, negotiations, conferences, or consulting with business associates.',
  },
  {
    icon: (<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0z" /><path d="M12 6v6l4 2" /></svg>),
    title: 'B1/B2 Combined',
    text: 'The most common visitor visa, covering both business and tourism purposes on one trip.',
  },
];

const plans: VisaPlan[] = [
  { id: 'us-standard', name: 'B1/B2 Visitor Visa — Standard', entryType: 'Multiple Entry', validity: 'Up to 10 years, nationality-dependent', stay: 'Typically up to 6 months per visit', processing: 'Interview wait time + 5–10 business days', price: '65', currency: 'OMR' },
  { id: 'us-assisted', name: 'B1/B2 Visitor Visa — Fully Assisted', entryType: 'Multiple Entry', validity: 'Up to 10 years, nationality-dependent', stay: 'Typically up to 6 months per visit', processing: 'Interview wait time + 5–10 business days', price: '120', currency: 'OMR', featured: true },
  { id: 'us-interview-prep', name: 'Interview Preparation Only', entryType: 'N/A', validity: 'N/A', stay: 'N/A', processing: 'Single coaching session', price: '45', currency: 'OMR' },
];

const requirements: RequirementItem[] = [
  { label: 'Form DS-160 confirmation', detail: 'Completed online nonimmigrant visa application, with the confirmation page printed for your interview.' },
  { label: 'Passport', detail: 'Valid for at least 6 months beyond your intended stay in the United States.' },
  { label: 'Photo', detail: 'One recent photo meeting U.S. visa photo specifications, uploaded with your DS-160.' },
  { label: 'Visa fee payment receipt', detail: 'Proof of payment for the MRV (Machine Readable Visa) application fee.' },
  { label: 'Ties to home country', detail: 'Evidence of employment, property, or family ties showing your intent to return — a common focus of the interview.' },
  { label: 'Financial evidence', detail: 'Bank statements or sponsorship letters showing you can cover the cost of your trip.' },
  { label: 'Travel purpose documents', detail: 'Invitation letters, conference registration, or a trip itinerary depending on your visit type.' },
];

const goodToKnow: string[] = [
  'Almost all B1/B2 applicants must attend an in-person interview at the U.S. Embassy or Consulate — we prepare you thoroughly for it.',
  'Visa approval doesn\'t guarantee entry; U.S. Customs and Border Protection makes the final decision at the port of entry.',
  'Interview wait times vary significantly by location and season — we track current wait times when booking your appointment.',
  'A visa refusal under Section 214(b) (failure to show sufficient ties abroad) can often be addressed in a stronger follow-up application — we\'ll help you understand what to strengthen.',
];

const steps: ProcessStep[] = [
  { title: 'Consultation & eligibility review', text: 'We assess your travel purpose and history to identify the right visa category and likely areas of scrutiny.' },
  { title: 'DS-160 & documentation', text: 'We help you complete the DS-160 accurately and assemble supporting documents for your interview.' },
  { title: 'Fee payment & appointment booking', text: 'We handle the MRV fee payment and book your biometric and interview appointments.' },
  { title: 'Interview preparation', text: 'A dedicated session covering likely questions, common pitfalls, and how to present your case clearly.' },
  { title: 'Interview day & decision', text: 'You attend your interview at the embassy; most applicants receive a decision the same day.' },
];

const faqs: FaqItem[] = [
  { question: 'Do I really need an interview every time?', answer: 'Most first-time applicants and many renewals require an interview. Some renewal applicants may qualify for an interview waiver — we\'ll check your eligibility before booking.' },
  { question: 'What\'s the most common reason for a US visitor visa refusal?', answer: 'The most frequent refusal reason is failing to demonstrate sufficiently strong ties to your home country. Our interview preparation focuses heavily on this.' },
  { question: 'How long is the visa actually valid once approved?', answer: 'Visa validity depends on your nationality and can range from 1 to 10 years, but each individual visit is still limited to the duration granted by the border officer on arrival, typically up to 6 months.' },
  { question: 'Can I apply again after a refusal?', answer: 'Yes, there\'s no mandatory waiting period, but reapplying without addressing the original concern rarely changes the outcome. We\'ll help you build a stronger case before you try again.' },
  { question: 'Do children need to attend the interview too?', answer: 'Policies vary by embassy — some allow children under a certain age to skip the interview if a parent applies alongside them. We\'ll confirm the specific rule for your local embassy.' },
];

export default function VisaUsaPage() {
  const { plans, loading, error } = useVisaPacksByCountry('USA');
  return (
    <VisaPageLayout2
      countryName="the United States"
      flagEmoji="🇺🇸"
      heroEyebrow="United States of America"
      heroImage="https://images.unsplash.com/photo-1760974015791-cf0dd376aa18?w=1600&q=80&fit=crop&crop=center"
      heroTitleLead="Your"
      heroTitleHighlight="US"
      heroTitleTail="visitor visa, prepared and rehearsed"
      heroSubtitle="From New York's skyline to national parks out west — the interview is the part that decides everything. We prepare your documents and your answers, together."
      trustStats={[
        { value: '5–10', label: 'Business Days After Interview' },
        { value: '89%', label: 'Approval Rate' },
        { value: '1,400+', label: 'US Visas Issued' },
      ]}
      categories={categories}
      plans={plans}
    
      requirements={requirements}
      goodToKnow={goodToKnow}
      steps={steps}
      useTimeline
      faqs={faqs}
      ctaSubtitle="Book a consultation — we'll review your travel history and profile honestly, then prepare you for the strongest possible interview."
    />
  );
}